import React from "react";
import { Box, Button, Chip, Divider, Typography, Dialog } from "@mui/material";
import toast from "react-hot-toast";
import { IUser } from "../../utils/types";
import { TextView } from "../../components/TextView/TextView";
import { UserTabEnum } from "./types";
import HistoryIcon from "@mui/icons-material/History";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { ViewUserLoginHistory } from "./ViewUserLoginHistory";
import { userFriendlyDateTimestamp } from "../../utils/timestamps";
import { APP_Z_INDEX } from "../../utils/zIndex";
import { useResetTrialMutation } from "../../api/apiSlice";
import { ConfirmDialog } from "../../components/Dialogs/ConfirmDialog";
import { getApiErrorMessage } from "../../utils";

interface Props {
  activeTab: UserTabEnum;
  user: IUser | null;
  handleClose: () => void;
}

export const ViewUser: React.FC<Props> = props => {
  const { user, handleClose } = props;

  const [openUserHistory, setOpenUserHistory] = React.useState<boolean>(false);
  const [confirmResetOpen, setConfirmResetOpen] = React.useState(false);
  const [resetTrial, { isLoading: isResetting }] = useResetTrialMutation();

  if (!user) {
    return (
      <Typography variant="h5" component="h2">
        No user selected
      </Typography>
    );
  }

  const handleOpenUserHistory = () => {
    setOpenUserHistory(true);
  };

  const handleCloseUserHistory = () => {
    setOpenUserHistory(false);
  };

  const handleResetTrial = async () => {
    if (isResetting) return;
    setConfirmResetOpen(false);
    try {
      await resetTrial(String(user.id)).unwrap();
      toast.success("Trial reset. User can re-trigger a trial on next signup.");
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e, "Failed to reset trial."));
    }
  };

  const LoginHistoryIcon: React.ReactNode = (
    <Button onClick={handleOpenUserHistory}>
      <Box display="flex" alignItems="center" gap={0.5}>
        <HistoryIcon color="primary" fontSize="small" />
        <Typography color="primary" variant="caption">
          Login History
        </Typography>
      </Box>
    </Button>
  );

  return (
    <Box display="flex" flexDirection="column" justifyContent="space-between" sx={{ width: "100%" }}>
      <Dialog open={openUserHistory} onClose={handleCloseUserHistory} fullWidth maxWidth="lg" sx={{ zIndex: APP_Z_INDEX.drawerDialog }}>
        <ViewUserLoginHistory handleClose={handleCloseUserHistory} user={user} />
      </Dialog>
      <Box>
        <TextView label="Full Name" value={user.name} />
        <TextView label="Email" value={user.email} />
        <TextView
          label="Roles"
          value={
            user.roles?.length ? (
              <Box display="flex" sx={{ gap: 1 }}>
                {user.roles.map(({ role, id }) => (
                  <Chip size="small" key={id} label={role} />
                ))}
              </Box>
            ) : (
              "User has no roles"
            )
          }
        />
        <TextView label="Status" value={user.status.charAt(0).toUpperCase() + user.status.slice(1)} />
        <TextView label="Created At" value={userFriendlyDateTimestamp(user.createdAt)} />
        <TextView label="Last Login" value={userFriendlyDateTimestamp(user.lastLoginAt || "")} actionComponent={LoginHistoryIcon} />
        <TextView label="Subscribed Date" value={userFriendlyDateTimestamp(user.subscribedAt || "")} />
        <TextView label="Subscription Status" value={user.subscriptionStatus?.replace(/_/g, " ") || "-"} />
        <TextView label="Billing Period Start" value={userFriendlyDateTimestamp(user.subscriptionStartAt || "")} />
        <TextView label="Billing Period End" value={userFriendlyDateTimestamp(user.subscriptionEndAt || "")} />
        <TextView label="Unsubscribed Date" value={userFriendlyDateTimestamp(user.unsubscribedAt || "")} />
        <TextView label="Total Shares" value={user.totalShares} />
        <TextView label="Total Likes" value={user.totalLikes} />
        <TextView label="Total Hides" value={user.totalHides} />
        <TextView label="Cultures Subscribed" value={user.totalCulturesSubscribed || 0} />
      </Box>
      <Box sx={{ gap: 2 }} display="flex" flexDirection="column" justifyContent="space-between">
        <Divider />
        <Box display="flex" justifyContent="flex-end" sx={{ position: "relative", bottom: 0, gap: 3 }}>
          <Button
            disableElevation
            variant="outlined"
            sx={{ borderRadius: "20px" }}
            onClick={() => setConfirmResetOpen(true)}
            disabled={isResetting}
            startIcon={<RestartAltIcon />}
          >
            <Typography sx={{ fontSize: "16px" }}>
              Reset Trial
            </Typography>
          </Button>
          <Button disableElevation variant="outlined" sx={{ borderRadius: "20px" }} onClick={handleClose} type="button">
            <Typography sx={{ fontSize: "16px" }} color="textPrimary">
              Cancel
            </Typography>
          </Button>
        </Box>
      </Box>
      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset trial"
        body={`Delete the trial record for "${user.name}"? They will get a new trial on their next signup.`}
        onClose={() => setConfirmResetOpen(false)}
        onConfirm={handleResetTrial}
        loading={isResetting}
      />
    </Box>
  );
};
